import { AspectMaintainer, Select, Switch } from "@clintonelec/react-storybook";
import {
	BacklightCompensation, backlightCompensationOptions, type DefogControl, defogControlOptions,
	type DigitalNoiseReduction, digitalNoiseReductionOptions, type EnhancementActivation, enhancementActivationOptions,
	ExposureMode, HighlightCompensation, highlightCompensationOptions
} from "Data/Objects/Camera";
import { produce } from "immer";
import type { ImageSettingsState } from "Interfaces";
import { type Dispatch, memo, type SetStateAction } from "react";

type EnhancementProps = {
	localSettings: ImageSettingsState;
	renderPopover: (message: string) => React.ReactElement;
	setLocalSettings: Dispatch<SetStateAction<ImageSettingsState>>;
};

function Enhancement(props: EnhancementProps) {
	const { localSettings, setLocalSettings, renderPopover } = props;
	const {
		wideDynamicRange, backlightCompensation, highlightCompensation, digitalNoiseReduction, defogControl,
		enhancementActivation
	} = localSettings.enhancement;

	const isManualExposure = localSettings.exposure.exposureMode === ExposureMode.MANUAL;

	const handleWideDynamicRangeChecked = (newChecked: boolean) => {
		setLocalSettings(produce(localSettings, draft => {
			draft.enhancement.wideDynamicRange = newChecked;
		}));
	};

	const handleBacklightCompensationSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.enhancement.backlightCompensation = value as BacklightCompensation;
		}));
	};

	const handleHighlightCompensationSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.enhancement.highlightCompensation = value as HighlightCompensation;
		}));
	};

	const handleDigitalNoiseReductionSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.enhancement.digitalNoiseReduction = value as DigitalNoiseReduction;
		}));
	};

	const handleDefogControlSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.enhancement.defogControl = value as DefogControl;
		}));
	};

	const handleEnhancementActivationSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.enhancement.enhancementActivation = value as EnhancementActivation;
		}));
	};

	const renderInfoIcon = () => {
		if (isManualExposure) {
			return renderPopover("To use this setting, go to Exposure tab and change 'Exposure Mode'");
		}
	};

	return (
		<div className="two-column-card-content">
			<div className="left-content">
				<AspectMaintainer>
					<div className="camera-snapshot"></div>
				</AspectMaintainer>
			</div>
			<div className="right-content">
				<div className="form-row">
					<span>Wide Dynamic Range</span>
					{ renderInfoIcon() }
					<div className="form-switch-wrapper">
						<Switch
							disabled={ isManualExposure }
							checked={ wideDynamicRange }
							onChange={ handleWideDynamicRangeChecked }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Backlight Compensation</span>
					{ renderInfoIcon() }
					<Select
						allowClear={ false }
						className="form-select"
						disabled={
							isManualExposure
							|| localSettings.enhancement.highlightCompensation !== HighlightCompensation.OFF
						}
						onSelect={ handleBacklightCompensationSelect }
						options={ backlightCompensationOptions }
						value={ backlightCompensation }
					/>
				</div>
				<div className="form-row">
					<span>Highlight Compensation</span>
					{ renderInfoIcon() }
					<Select
						allowClear={ false }
						className="form-select"
						disabled={
							isManualExposure
							|| localSettings.enhancement.backlightCompensation !== BacklightCompensation.OFF
						}
						onSelect={ handleHighlightCompensationSelect }
						options={ highlightCompensationOptions }
						value={ highlightCompensation }
					/>
				</div>
				<div className="form-row">
					<span>Digital Noise Reduction</span>
					<Select
						allowClear={ false }
						className="form-select"
						onSelect={ handleDigitalNoiseReductionSelect }
						options={ digitalNoiseReductionOptions }
						value={ digitalNoiseReduction }
					/>
				</div>
				<div className="form-row">
					<span>Defog Control</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ wideDynamicRange }
						onSelect={ handleDefogControlSelect }
						options={ defogControlOptions }
						value={ defogControl }
					/>
				</div>
				<div className="form-row">&nbsp;</div>
				<div className="form-row">
					<span>Activation</span>
					<Select
						allowClear={ false }
						className="form-select"
						onSelect={ handleEnhancementActivationSelect }
						options={ enhancementActivationOptions }
						value={ enhancementActivation }
					/>
				</div>
			</div>
		</div>
	);
}

export default memo(Enhancement);
